import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Zap,
  ChevronUp,
  X,
  RotateCcw,
  AlertTriangle,
  Users,
  User,
  MessageSquare,
  ShieldAlert,
  Search,
  FileText,
} from "lucide-react";
import { useAiUsage } from "../hooks/useAiUsage";
import { formatUsdCost } from "../services/aiUsageService";
import "./AiUsageMeter.css";

const getServiceDetails = (srvKey, t) => {
  switch (srvKey) {
    case "query_research":
      return {
        name: t("footer.serviceQueryResearch", "Prompt Research"),
        unit: t("footer.unitQueries", "queries"),
        icon: Search,
      };
    case "optimize_content":
      return {
        name: t("footer.serviceOptimizeContent", "Content Optimizer"),
        unit: t("footer.unitBriefs", "briefs & drafts"),
        icon: FileText,
      };
    case "ai_assistant":
    default:
      return {
        name: t("footer.serviceAiAssistant", "AI Assistant"),
        unit: t("footer.unitResponses", "chat messages"),
        icon: MessageSquare,
      };
  }
};

const AiUsageMeter = () => {
  const { t } = useTranslation();
  const {
    userUsage,
    clientUsage,
    isLoading,
    refresh,
    quotaStatus,
    userQuotaStatus,
    isAdmin,
    isSuperAdmin,
    username,
  } = useAiUsage();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("team"); // "team" | "my"
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Close popover when clicking outside or pressing Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Aggregate team metrics (isolated strictly from personal metrics)
  const totalCost = clientUsage?.total_cost_usd ?? 0;
  const totalRequests =
    clientUsage?.total_requests ?? clientUsage?.total_conversations ?? 0;
  const teamServices = clientUsage?.services || null;

  // Personal metrics
  const myCost = userUsage?.total_cost_usd ?? 0;
  const myRequests =
    userUsage?.total_requests ?? userUsage?.conversation_count ?? 0;
  const myMessages = userUsage?.message_count ?? myRequests;
  const mySessions = userUsage?.conversations || [];
  const myServices = userUsage?.services || null;

  // Team users (for admin breakdown)
  const teamUsers = clientUsage?.users || [];

  const displayCost = isAdmin ? totalCost : myCost;
  const displayQuota = isAdmin ? quotaStatus : userQuotaStatus;
  const percentage = displayQuota.percentage;
  const statusClass = `status-${displayQuota.status}`;
  const formattedQuota = formatUsdCost(displayQuota.quota).replace(/\.00$/, "");
  const formattedQuotaWithDecimals = formatUsdCost(displayQuota.quota);

  return (
    <div className="ai-usage-meter-container">
      {/* Footer Pill Trigger */}
      <button
        ref={triggerRef}
        type="button"
        className={`ai-usage-meter-trigger ${isSuperAdmin ? statusClass : "status-normal"} ${isOpen ? "active" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-testid="ai-usage-meter-trigger"
        title={t("footer.usageTooltip", "Click to view AI usage and monthly quota details")}
      >
        <span className="ai-usage-meter-icon-wrap">
          {isSuperAdmin && (displayQuota.status === "danger" || displayQuota.status === "blocked") ? (
            <AlertTriangle size={13} className="ai-usage-icon alert" />
          ) : (
            <Zap size={13} className="ai-usage-icon" />
          )}
        </span>

        <span className="ai-usage-meter-label">
          {t("footer.aiUsage", "AI Usage")}
        </span>

        <span className="ai-usage-meter-value">
          {isSuperAdmin ? (
            `${formatUsdCost(displayCost)} / ${formatUsdCost(displayQuota.quota).replace(/\.00$/, "")}`
          ) : (
            `${Math.round(displayCost * 100).toLocaleString()} ${t("footer.credits", "credits")}`
          )}
        </span>

        {isSuperAdmin && (
          <>
            {/* Micro Progress Bar */}
            <div
              className="ai-usage-micro-bar-track"
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-label={t("footer.quotaProgress", "Monthly AI Quota Progress")}
            >
              <div
                className={`ai-usage-micro-bar-fill ${statusClass}`}
                style={{ width: `${Math.min(100, Math.max(2, percentage))}%` }}
              />
            </div>

            <span className="ai-usage-meter-percent">{percentage}%</span>
          </>
        )}

        <ChevronUp
          size={12}
          className={`ai-usage-meter-chevron ${isOpen ? "open" : ""}`}
        />
      </button>

      {/* Upward Floating Popover Card */}
      {isOpen && (
        <div
          ref={popoverRef}
          className="ai-usage-popover"
          role="dialog"
          aria-modal="false"
          aria-labelledby="ai-usage-popover-title"
          data-testid="ai-usage-popover"
        >
          {/* Header */}
          <div className="ai-usage-popover-header">
            <div className="d-flex align-items-center gap-2">
              <Zap size={16} className={`ai-usage-header-icon ${isSuperAdmin ? statusClass : "status-normal"}`} />
              <h6 id="ai-usage-popover-title" className="ai-usage-popover-title">
                {t("footer.popoverTitle", "AI Assistant Usage & Quota")}
              </h6>
            </div>
            <div className="d-flex align-items-center gap-2">
              {isSuperAdmin && (
                <span className={`ai-usage-badge ${statusClass}`} data-testid="usage-status-badge">
                  {percentage >= 99
                    ? t("footer.blockedBadge", "Blocked (99%)")
                    : percentage >= 90
                    ? t("footer.criticalBadge", "Critical (≥90%)")
                    : percentage >= 70
                    ? t("footer.warningBadge", "Warning (≥70%)")
                    : t("footer.normalBadge", "Normal")}
                </span>
              )}
              <button
                type="button"
                className="ai-usage-popover-close-btn"
                onClick={() => setIsOpen(false)}
                aria-label={t("common.close", "Close")}
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Quota Threshold Warning Banner */}
          {isSuperAdmin && quotaStatus.isBlocked && (
            <div className="ai-usage-alert-banner banner-blocked" data-testid="quota-banner-blocked">
              <ShieldAlert size={15} />
              <div>
                <strong>{t("footer.blockedTitle", "AI Assistant Blocked")}</strong>
                <p>
                  {t(
                    "footer.blockedMessage",
                    "Monthly quota has reached 99%. Further AI assistant requests are blocked by the backend until the next reset."
                  )}
                </p>
              </div>
            </div>
          )}

          {!isSuperAdmin && quotaStatus.isBlocked && (
            <div className="ai-usage-alert-banner banner-blocked" data-testid="quota-banner-blocked">
              <ShieldAlert size={15} />
              <div>
                <strong>{t("footer.blockedTitle", "AI Assistant Blocked")}</strong>
                <p>
                  {t(
                    "footer.blockedMessageSimplified",
                    "AI Assistant is temporarily unavailable due to high workspace activity. Please contact your administrator."
                  )}
                </p>
              </div>
            </div>
          )}

          {isSuperAdmin && percentage >= 90 && percentage < 99 && (
            <div className="ai-usage-alert-banner banner-danger" data-testid="quota-banner-danger">
              <AlertTriangle size={15} />
              <div>
                <strong>{t("footer.dangerTitle", "High Usage Alert (≥90%)")}</strong>
                <p>
                  {t(
                    "footer.dangerMessage",
                    "You have used 90% or more of the {{quota}} monthly quota. Requests will be blocked at 99%.",
                    { quota: formattedQuota }
                  )}
                </p>
              </div>
            </div>
          )}

          {isSuperAdmin && percentage >= 70 && percentage < 90 && (
            <div className="ai-usage-alert-banner banner-warning" data-testid="quota-banner-warning">
              <AlertTriangle size={15} />
              <div>
                <strong>{t("footer.warningTitle", "Approaching Quota Limit (≥70%)")}</strong>
                <p>
                  {t(
                    "footer.warningMessage",
                    "You have exceeded 70% of the {{quota}} monthly quota.",
                    { quota: formattedQuota }
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Navigation Tabs (Only rendered for Admins who have team-wide access) */}
          {isAdmin && (
            <div className="ai-usage-popover-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "team"}
                className={`ai-usage-tab-btn ${activeTab === "team" ? "active" : ""}`}
                onClick={() => setActiveTab("team")}
                data-testid="tab-team-usage"
              >
                <Users size={13} className="me-1" />
                {t("footer.teamUsageTab", "Team Usage")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "my"}
                className={`ai-usage-tab-btn ${activeTab === "my" ? "active" : ""}`}
                onClick={() => setActiveTab("my")}
                data-testid="tab-my-usage"
              >
                <User size={13} className="me-1" />
                {t("footer.myUsageTab", "My Usage")}
              </button>
            </div>
          )}

          <div className="ai-usage-popover-body">
            {isAdmin && activeTab === "team" ? (
              /* ── TEAM USAGE VIEW ───────────────────────────────── */
              <div className="ai-usage-team-view">
                {/* Team Quota Progress Card / Simplified Team AI Usage Card */}
                {!isSuperAdmin ? (
                  <div className="ai-usage-summary-card">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="ai-usage-summary-label">
                        {t("footer.teamAiUsage", "Team AI Usage")}
                      </span>
                      <span className="ai-usage-summary-cost">
                        <strong>{Math.round(totalCost * 100).toLocaleString()}</strong> {t("footer.credits", "credits")}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="ai-usage-summary-card">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="ai-usage-summary-label">
                        {t("footer.monthlyQuotaUsed", "Monthly Quota Used")}
                      </span>
                      <span className="ai-usage-summary-cost">
                        <strong>{formatUsdCost(totalCost)}</strong> / ${quotaStatus.quota.toFixed(2)} USD
                      </span>
                    </div>

                    {/* Progress Bar with 70% and 90% threshold marks */}
                    <div className="ai-usage-bar-track">
                      <div
                        className={`ai-usage-bar-fill ${statusClass}`}
                        style={{ width: `${Math.min(100, Math.max(1, percentage))}%` }}
                      />
                      <div className="ai-usage-threshold-marker mark-70" title="70% Warning" />
                      <div className="ai-usage-threshold-marker mark-90" title="90% Critical" />
                      <div className="ai-usage-threshold-marker mark-99" title="99% Blocked" />
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <small className="text-muted">
                        {t("footer.remainingQuota", "Remaining:")}{" "}
                        <strong className="text-dark">
                          ${quotaStatus.remainingUsd.toFixed(2)} USD
                        </strong>
                      </small>
                      <small className="ai-usage-percent-tag">
                        {percentage}% {t("footer.consumed", "consumed")}
                      </small>
                    </div>
                  </div>
                )}

                {/* Team KPI Grid */}
                <div className="ai-usage-kpi-grid">
                  <div className="ai-usage-kpi-tile">
                    <span className="kpi-label">
                      {isSuperAdmin
                        ? t("footer.totalSpend", "Total Spend")
                        : t("footer.creditsUsed", "Credits Used")}
                    </span>
                    <span className="kpi-value">
                      {isSuperAdmin
                        ? formatUsdCost(totalCost)
                        : `${Math.round(totalCost * 100).toLocaleString()}`}
                    </span>
                    {isSuperAdmin ? (
                      <span className="kpi-subtext">
                        ${quotaStatus.remainingUsd.toFixed(2)} {t("footer.remaining", "remaining")}
                      </span>
                    ) : (
                      <span className="kpi-subtext">
                        {t("footer.activeServiceUsage", "Active service usage")}
                      </span>
                    )}
                  </div>
                  <div className="ai-usage-kpi-tile">
                    <span className="kpi-label">{t("footer.totalActivity", "Total Activity")}</span>
                    <span className="kpi-value">{totalRequests}</span>
                    <span className="kpi-subtext">
                      {t("footer.acrossServices", "across all AI services")}
                    </span>
                  </div>
                </div>

                {/* Team Usage by Service Breakdown */}
                {teamServices && Object.keys(teamServices).length > 0 && (
                  <div className="ai-usage-breakdown-section mb-3">
                    <h6 className="section-title mb-2">
                      {t("footer.serviceBreakdownTitle", "Usage by Service")}
                    </h6>
                    <div className="ai-usage-sessions-list" data-testid="team-services-list">
                      {Object.entries(teamServices).map(([srvKey, srvData]) => {
                        const { name, unit, icon: Icon } = getServiceDetails(srvKey, t);
                        return (
                          <div key={srvKey} className="ai-usage-session-item">
                            <div className="session-info">
                              <span className="session-title">
                                <Icon size={12} className="me-1 text-muted" />
                                {name}
                              </span>
                              <span className="session-meta">
                                {srvData.request_count ?? 0} {unit}
                              </span>
                            </div>
                            <span className="session-cost">
                              {isSuperAdmin
                                ? formatUsdCost(srvData.total_cost_usd)
                                : `${Math.round((srvData.total_cost_usd ?? 0) * 100).toLocaleString()} ${t("footer.creditsShort", "credits")}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Individual Users Breakdown (Admins Only) */}
                <div className="ai-usage-breakdown-section">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="section-title mb-0">
                      {t("footer.userBreakdownTitle", "Team Member Breakdown")}
                    </h6>
                    {isAdmin && (
                      <span className="badge bg-light text-secondary border">
                        {teamUsers.length} {teamUsers.length === 1 ? "user" : "users"}
                      </span>
                    )}
                  </div>

                  {isAdmin ? (
                    teamUsers.length > 0 ? (
                      <div className="ai-usage-users-table-wrap">
                        <table className="ai-usage-users-table" data-testid="team-users-table">
                          <thead>
                            <tr>
                              <th>{t("footer.colUser", "User")}</th>
                              <th className="text-end">{t("footer.colRequests", "Requests")}</th>
                              <th className="text-end">
                                {isSuperAdmin
                                  ? t("footer.colCost", "Total Spend")
                                  : t("footer.credits", "credits")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {teamUsers.map((u) => (
                              <tr
                                key={u.username}
                                className={u.username === username ? "current-user-row" : ""}
                              >
                                <td className="user-cell" title={u.username}>
                                  <span className="user-name-text">
                                    {u.username}
                                    {u.username === username && (
                                      <span className="you-badge ms-1">
                                        {t("common.you", "You")}
                                      </span>
                                    )}
                                  </span>
                                </td>
                                <td className="text-end text-muted">{u.total_requests ?? u.conversation_count ?? 0}</td>
                                <td className="text-end fw-semibold text-dark">
                                  {isSuperAdmin
                                    ? formatUsdCost(u.total_cost_usd)
                                    : `${Math.round((u.total_cost_usd ?? 0) * 100).toLocaleString()}`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="ai-usage-empty-note">
                        {t("footer.noTeamActivity", "No team activity recorded for this period.")}
                      </p>
                    )
                  ) : (
                    /* Non-admin notice */
                    <div className="ai-usage-admin-only-notice" data-testid="non-admin-notice">
                      <p className="mb-0">
                        {t(
                          "footer.adminOnlyNotice",
                          "Total team usage is shown above. Individual usage breakdown is visible to workspace administrators."
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* ── MY USAGE VIEW ─────────────────────────────────── */
              <div className="ai-usage-my-view">
                {/* Personal Summary Card / Simplified Personal AI Usage Card */}
                {!isSuperAdmin ? (
                  <div className="ai-usage-summary-card">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="ai-usage-summary-label">
                        {t("footer.myUsageTotal", "Personal AI Usage")}
                      </span>
                      <span className="ai-usage-summary-cost">
                        <strong>{Math.round(myCost * 100).toLocaleString()}</strong> {t("footer.credits", "credits")}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="ai-usage-summary-card">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="ai-usage-summary-label">
                        {t("footer.myCostTotal", "Personal Spend")}
                      </span>
                      <span className="ai-usage-summary-cost">
                        <strong>{formatUsdCost(myCost)}</strong> USD
                      </span>
                    </div>

                    <div className="ai-usage-bar-track">
                      <div
                        className={`ai-usage-bar-fill status-${userQuotaStatus.status}`}
                        style={{ width: `${Math.min(100, Math.max(1, userQuotaStatus.percentage))}%` }}
                      />
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <small className="text-muted">
                        {myRequests} {t("footer.requestsShort", "requests")}
                      </small>
                      <small className="text-muted">
                        {userQuotaStatus.percentage}%{" "}
                        {t("footer.ofTeamQuota", "of {{quota}} quota", {
                          quota: formattedQuota,
                        })}
                      </small>
                    </div>
                  </div>
                )}

                {/* Personal KPI Grid */}
                <div className="ai-usage-kpi-grid">
                  <div className="ai-usage-kpi-tile">
                    <span className="kpi-label">
                      {isSuperAdmin
                        ? t("footer.myCostTotal", "Personal Spend")
                        : t("footer.myUsageTotal", "Personal AI Usage")}
                    </span>
                    <span className="kpi-value">
                      {isSuperAdmin
                        ? formatUsdCost(myCost)
                        : `${Math.round(myCost * 100).toLocaleString()}`}
                    </span>
                    {isSuperAdmin ? (
                      <span className="kpi-subtext">
                        {userQuotaStatus.percentage}%{" "}
                        {t("footer.ofTeamQuota", "of {{quota}} quota", {
                          quota: formattedQuota,
                        })}
                      </span>
                    ) : (
                      <span className="kpi-subtext">
                        {t("footer.creditsShort", "credits")}
                      </span>
                    )}
                  </div>
                  <div className="ai-usage-kpi-tile">
                    <span className="kpi-label">{t("footer.myActivity", "Personal Activity")}</span>
                    <span className="kpi-value">{myRequests}</span>
                    <span className="kpi-subtext">{myMessages} {t("footer.responses", "responses")}</span>
                  </div>
                </div>

                {/* Per-Service Breakdown (When provided by /api/v1/ai-usage) */}
                {myServices && Object.keys(myServices).length > 0 && (
                  <div className="ai-usage-breakdown-section mb-3">
                    <h6 className="section-title mb-2">
                      {t("footer.serviceBreakdownTitle", "Usage by Service")}
                    </h6>
                    <div className="ai-usage-sessions-list" data-testid="my-services-list">
                      {Object.entries(myServices).map(([srvKey, srvData]) => {
                        const { name, unit, icon: Icon } = getServiceDetails(srvKey, t);
                        return (
                          <div key={srvKey} className="ai-usage-session-item">
                            <div className="session-info">
                              <span className="session-title">
                                <Icon size={12} className="me-1 text-muted" />
                                {name}
                              </span>
                              <span className="session-meta">
                                {srvData.request_count ?? 0} {unit}
                              </span>
                            </div>
                            <span className="session-cost">
                              {isSuperAdmin
                                ? formatUsdCost(srvData.total_cost_usd)
                                : `${Math.round((srvData.total_cost_usd ?? 0) * 100).toLocaleString()} ${t("footer.creditsShort", "credits")}`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Personal Recent Conversations */}
                <div className="ai-usage-breakdown-section">
                  <h6 className="section-title mb-2">
                    {t("footer.recentSessionsTitle", "Recent Conversations")}
                  </h6>
                  {mySessions.length > 0 ? (
                    <div className="ai-usage-sessions-list" data-testid="my-sessions-list">
                      {mySessions.slice(0, 5).map((s) => (
                        <div key={s.conversation_id} className="ai-usage-session-item">
                          <div className="session-info">
                            <span className="session-title">
                              <MessageSquare size={12} className="me-1 text-muted" />
                              {s.title || t("footer.untitledChat", "Conversation")}
                            </span>
                            <span className="session-meta">
                              {s.message_count} {t("footer.messagesShort", "msgs")}
                            </span>
                          </div>
                          <span className="session-cost">
                            {isSuperAdmin
                              ? formatUsdCost(s.cost_usd)
                              : `${Math.round((s.cost_usd ?? 0) * 100).toLocaleString()} ${t("footer.creditsShort", "credits")}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="ai-usage-empty-note">
                      {t("footer.noSessionsYet", "No conversations recorded yet.")}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Popover Footer Bar */}
          <div className="ai-usage-popover-footer">
            {isSuperAdmin ? (
              <span className="quota-disclaimer">
                {t(
                  "footer.quotaDisclaimer",
                  "Monthly quota: {{quota}} USD (blocked at 99%)",
                  { quota: formattedQuotaWithDecimals }
                )}
              </span>
            ) : (
              <span className="quota-disclaimer text-secondary">
                {t("footer.billingCycleReset", "Credits reset at the start of next calendar month")}
              </span>
            )}
            <button
              type="button"
              className="ai-usage-refresh-btn"
              onClick={refresh}
              disabled={isLoading}
              title={t("footer.refreshUsage", "Refresh usage metrics")}
              data-testid="refresh-usage-btn"
            >
              <RotateCcw size={11} className={isLoading ? "spin" : ""} />
              <span>{t("common.refresh", "Refresh")}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiUsageMeter;
